import {
  createProtocolMutation,
  deleteProtocolMutation,
  updateProtocolMutation,
} from '@/api/protocols/protocolMutations';
import { getProtocolsQuery } from '@/api/protocols/protocolQueries';
import { queryKeys } from '@/api/queryKeys';
import { Button } from '@/components/common/Button';
import { ConfirmDeleteDialog } from '@/components/common/ConfirmDeleteDialog';
import { RelationActionButton } from '@/components/common/RelationActionButton';
import { TableCell, TableHead } from '@/components/common/Table';
import type { Protocol } from '@/domain/Protocol';
import { getStorageUrl } from '@/lib/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { DashboardTableAccordion } from '../../DashboardTableAccordion';
import { ProtocolsDialog } from './ProtocolsDialog';

export const Protocols = () => {
  const [page, setPage] = useState(1);
  const [editingProtocolId, setEditingProtocolId] = useState<number | null>(
    null,
  );
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [protocolIdToDelete, setProtocolIdToDelete] = useState<number | null>(
    null,
  );

  const queryClient = useQueryClient();
  const { data: response, isLoading } = useQuery(getProtocolsQuery(page));

  const protocols = response?.data || [];
  const meta = response?.meta;

  const { mutate: createProtocol, isPending: isCreating } = useMutation({
    ...createProtocolMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.adminProtocols.all(),
      });
      toast.success('Protocol created successfully');
      setIsDialogOpen(false);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to create protocol');
    },
  });

  const { mutate: updateProtocol, isPending: isUpdating } = useMutation({
    ...updateProtocolMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.adminProtocols.all(),
      });
      toast.success('Protocol updated successfully');
      setIsDialogOpen(false);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update protocol');
    },
  });

  const { mutate: deleteProtocol, isPending: isDeleting } = useMutation({
    ...deleteProtocolMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.adminProtocols.all(),
      });
      toast.success('Protocol deleted successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete protocol');
    },
  });

  const [selectedProtocol, setSelectedProtocol] = useState<Protocol | null>(
    null,
  );

  const handleAddNew = () => {
    setSelectedProtocol(null);
    setEditingProtocolId(null);
    setIsDialogOpen(true);
  };

  const handleEdit = (id: number) => {
    const p = protocols.find((item) => item.id === id) || null;
    setSelectedProtocol(p);
    setEditingProtocolId(id);
    setIsDialogOpen(true);
  };

  const handleDeleteClick = (id: number) => {
    setProtocolIdToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (protocolIdToDelete !== null) {
      deleteProtocol(protocolIdToDelete);
      setDeleteDialogOpen(false);
      setProtocolIdToDelete(null);
    }
  };

  const handleSubmit = (data: Protocol, image?: File) => {
    if (editingProtocolId) {
      updateProtocol({ id: editingProtocolId, data, image });
    } else {
      createProtocol({ data, image });
    }
  };

  return (
    <>
      <DashboardTableAccordion
        value="Our protocols"
        title="Our protocols"
        data={protocols}
        isLoading={isLoading}
        paginationMeta={meta}
        onPageChange={setPage}
        emptyMessage="No protocols found"
        renderHeader={() => (
          <>
            <TableHead className="w-16"></TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Type</TableHead>
            <TableHead className="w-48" />
          </>
        )}
        renderRow={(protocol: Protocol) => (
          <>
            <TableCell>
              <div className="flex size-8 items-center justify-center overflow-hidden rounded-full bg-slate-100 text-[10px] italic">
                {protocol.image_url ? (
                  <img
                    src={getStorageUrl(protocol.image_url)}
                    alt=""
                    className="size-full object-cover"
                    onError={(e) => (e.currentTarget.style.display = 'none')}
                  />
                ) : (
                  'img'
                )}
              </div>
            </TableCell>
            <TableCell>{protocol.name}</TableCell>
            <TableCell>{protocol.type}</TableCell>
            <TableCell>
              <div className="flex justify-end gap-3">
                <RelationActionButton
                  type="button"
                  onClick={() => handleEdit(protocol.id)}
                >
                  Edit
                </RelationActionButton>
                <RelationActionButton
                  type="button"
                  onClick={() => handleDeleteClick(protocol.id)}
                  className="text-delete"
                >
                  Delete
                </RelationActionButton>
              </div>
            </TableCell>
          </>
        )}
        footerActions={
          <Button
            type="button"
            variant="primary"
            size="md"
            onClick={handleAddNew}
          >
            Create new protocol
          </Button>
        }
      />

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete protocol"
        description="Are you sure you want to delete this protocol?"
        onConfirm={handleDeleteConfirm}
        isPending={isDeleting}
      />

      <ProtocolsDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        protocol={selectedProtocol}
        mode={editingProtocolId ? 'edit' : 'add'}
        onSubmit={handleSubmit}
        isPending={isCreating || isUpdating}
      />
    </>
  );
};
